import type { FC, KeyboardEvent } from 'react'
import type { Plan } from '@/constants/pricing'

import { useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/kit/button'
import { Reveal } from '@/components/kit/reveal'
import { Spark } from '@/components/kit/spark'
import { plans } from '@/constants/pricing'

const money = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })

export const formatPrice = (value: number): string => `£${money.format(value)}`

const AnimatedPrice: FC<{ value: number }> = ({ value }) => {
    const ref = useRef<HTMLSpanElement>(null)
    const [initial] = useState(value)
    const shown = useRef(value)

    useEffect(() => {
        const target = ref.current

        if (!target || shown.current === value) return

        const from = shown.current
        const to = value

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            target.textContent = formatPrice(to)
            shown.current = to

            return
        }

        const begin = performance.now()
        let raf = 0

        const tick = (now: number) => {
            const progress = Math.min(1, (now - begin) / 650)
            const eased = 1 - Math.pow(1 - progress, 3)
            const current = Math.round(from + (to - from) * eased)

            target.textContent = formatPrice(current)
            shown.current = current

            if (progress < 1) raf = window.requestAnimationFrame(tick)
        }

        raf = window.requestAnimationFrame(tick)

        return () => window.cancelAnimationFrame(raf)
    }, [value])

    return (
        <span ref={ref}>
            {formatPrice(initial)}
        </span>
    )
}

export const BillingToggle: FC<{ yearly: boolean, onChange: (value: boolean) => void }> = ({ yearly, onChange }) => {
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
            event.preventDefault()
            onChange(!yearly)
        }
    }

    return (
        <div
            role='radiogroup'
            aria-label='Billing period'
            onKeyDown={onKeyDown}
            className='relative inline-flex rounded-full border border-black/[0.07] bg-paper p-1 shadow-[0_10px_30px_-20px_rgb(30_30_30/0.4)]'
        >
            <span
                aria-hidden='true'
                className={cn('absolute inset-y-1 left-1 w-[calc(50%_-_4px)] rounded-full bg-ink transition-transform duration-500 ease-expo', yearly && 'translate-x-full')}
            />

            {[false, true].map(option => {
                const selected = yearly === option

                return (
                    <button
                        key={String(option)}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(option)}
                        className={cn('relative z-10 inline-flex w-36 items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-colors duration-300', selected ? 'text-cream' : 'text-ink')}
                    >
                        {option ? 'Yearly' : 'Monthly'}
                        {option && (
                            <span className='rounded-full bg-ember px-1.5 py-0.5 font-mono text-[10px] text-ink'>
                                Save 18%
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}

const PlanCard: FC<{ plan: Plan, yearly: boolean }> = ({ plan, yearly }) => {
    const price = yearly ? plan.yearly : plan.monthly

    return (
        <article
            aria-labelledby={`plan-${plan.id}`}
            className={cn(
                'relative flex h-full flex-col rounded-[2rem] bg-paper p-7 md:p-8',
                plan.highlighted
                    ? 'border-2 border-ember shadow-[0_40px_80px_-46px_rgb(255_92_0/0.6)]'
                    : 'border border-black/5 shadow-[0_30px_60px_-50px_rgb(30_30_30/0.45)]'
            )}
        >
            {plan.highlighted && (
                <span className='absolute -top-3.5 left-7 inline-flex items-center gap-1.5 rounded-full bg-ember px-3 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-ink md:left-8'>
                    <Spark className='size-2.5' />
                    Most booked
                </span>
            )}

            <h2
                id={`plan-${plan.id}`}
                className='text-[2rem] font-bold leading-none tracking-[-0.035em]'
            >
                {plan.name}
            </h2>
            <p className='mt-3 min-h-12 text-[15px] leading-relaxed text-stone'>
                {plan.blurb}
            </p>

            <p className='mt-8 flex items-baseline gap-2'>
                <span className='font-heading text-[clamp(3.25rem,5vw,4.25rem)] font-bold leading-none tracking-[-0.04em]'>
                    <AnimatedPrice value={price} />
                </span>
                <span className='text-stone'>/ month</span>
            </p>
            <p className='mt-2 font-mono text-[11px] text-stone'>
                {yearly ? `Paid yearly, ${formatPrice(plan.yearly * 12)} in total` : 'Paid monthly, after the first quarter'}
            </p>

            <Button
                href={`/contact?plan=${plan.id}`}
                variant={plan.highlighted ? 'primary' : 'dark'}
                className='mt-8 w-full justify-between'
            >
                {plan.cta}
            </Button>

            <ul className='mt-8 flex flex-col gap-3 border-t border-ink/10 pt-8'>
                {plan.features.map(feature => (
                    <li
                        key={feature}
                        className='flex items-start gap-3 text-[15px] leading-snug'
                    >
                        <span className='mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-ember/20 text-ink'>
                            <Check
                                aria-hidden='true'
                                className='size-3'
                                strokeWidth={3}
                            />
                        </span>
                        {feature}
                    </li>
                ))}
            </ul>
        </article>
    )
}

export const PlanGrid: FC<{ yearly: boolean }> = ({ yearly }) => {
    return (
        <div className='grid gap-5 md:grid-cols-3 md:gap-4 lg:gap-5'>
            {plans.map((plan, index) => (
                <Reveal
                    key={plan.id}
                    delay={index * 100}
                    className={cn(plan.highlighted && 'md:-mt-4 md:mb-4')}
                >
                    <PlanCard
                        plan={plan}
                        yearly={yearly}
                    />
                </Reveal>
            ))}
        </div>
    )
}
